/**
 * MONSTER TOWN — THE WORLD GRAPH. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * The town, the house exterior, every interior room and every venue is a
 * `SceneNode`: its own `Grid`, its own three.js subtree. Exactly ONE scene is
 * awake at a time; the rest keep their state and their built art but are
 * detached from the render graph, so draw calls stay flat no matter how much a
 * kid builds (older-iPad rule).
 *
 * EVERY SceneNode has the same four layers, in this order. Put your objects in
 * the right one or another crew's code will fight you:
 *   ground    — terrain, floor plane, static base. Rebuilt rarely.
 *   statics   — the shell: house walls, roof, non-placeable structure.
 *   placeables— everything the kid placed. One child group per instance id.
 *   actors    — the player monster, NPCs, particles, ghosts. Never saved.
 *
 * DOOR TRANSITIONS: register a `Portal` on both sides. `transitionTo()` fires
 * `scene:transitionStart`, which BLOCKS SAVE WRITES until `scene:transitionEnd`.
 * Never write to save.ts yourself during a transition — it will be deferred and
 * you will think it was lost.
 */

import * as THREE from 'three'
import { bus } from './events'
import { Grid, type Cell, type GridSpec, type Rot } from './grid'
import type { InstanceId, SceneId } from './types'

export type SceneKind = 'exterior' | 'interior'

export interface SceneDef {
  readonly id: SceneId
  readonly kind: SceneKind
  readonly grid: GridSpec
  /** Shown behind the world. Interiors usually skip the sky. */
  readonly sky?: boolean
  /** Dev label. Never shown to a kid. */
  readonly devName: string
}

export interface Portal {
  readonly id: string
  readonly sceneId: SceneId
  /** Which cell you step on to use it. */
  readonly cell: Cell
  readonly toSceneId: SceneId
  /** Where you appear on the other side. */
  readonly toCell: Cell
  /** Camera rotation to snap to on arrival, or undefined to keep the current one. */
  readonly toRot?: Rot
}

export interface SceneNode {
  readonly def: SceneDef
  readonly grid: Grid
  readonly root: THREE.Group
  readonly ground: THREE.Group
  readonly statics: THREE.Group
  readonly placeables: THREE.Group
  readonly actors: THREE.Group
  /** instanceId -> its art group inside `placeables`. Owned by the build crew. */
  readonly art: Map<InstanceId, THREE.Object3D>
  awake: boolean
}

/** Supplied by the render/UI crew so transitions can fade. Optional. */
export type Fader = (dir: 'out' | 'in', ms: number) => Promise<void>

export interface TransitionOptions {
  /** Where the player lands. Defaults to the matching portal's `toCell`. */
  spawnCell?: Cell
  fadeMs?: number
  /** Skip the fade entirely (harness/tests). */
  instant?: boolean
}

export class WorldGraph {
  /** Add THIS to your THREE.Scene once. Everything hangs off it. */
  readonly root = new THREE.Group()

  private readonly scenes = new Map<SceneId, SceneNode>()
  private readonly portals: Portal[] = []
  private activeId: SceneId | null = null
  private busy = false
  private fader: Fader | null = null

  constructor() {
    this.root.name = 'worldGraph'
  }

  // -- registration ------------------------------------------------------

  register(def: SceneDef): SceneNode {
    const existing = this.scenes.get(def.id)
    if (existing !== undefined) return existing

    const mk = (name: string): THREE.Group => {
      const g = new THREE.Group()
      g.name = `${def.id}:${name}`
      return g
    }
    const root = mk('root')
    const ground = mk('ground')
    const statics = mk('statics')
    const placeables = mk('placeables')
    const actors = mk('actors')
    root.add(ground, statics, placeables, actors)
    root.visible = false

    const node: SceneNode = {
      def,
      grid: new Grid(def.grid),
      root,
      ground,
      statics,
      placeables,
      actors,
      art: new Map(),
      awake: false,
    }
    this.scenes.set(def.id, node)
    return node
  }

  has(id: SceneId): boolean {
    return this.scenes.has(id)
  }

  get(id: SceneId): SceneNode | undefined {
    return this.scenes.get(id)
  }

  require(id: SceneId): SceneNode {
    const n = this.scenes.get(id)
    if (n === undefined) throw new Error(`unknown scene "${id}"`)
    return n
  }

  ids(): SceneId[] {
    return [...this.scenes.keys()]
  }

  get activeSceneId(): SceneId | null {
    return this.activeId
  }

  /** Throws before the first transition. Call after `setActiveImmediate`. */
  get active(): SceneNode {
    if (this.activeId === null) throw new Error('no active scene yet')
    return this.require(this.activeId)
  }

  get transitioning(): boolean {
    return this.busy
  }

  setFader(f: Fader | null): void {
    this.fader = f
  }

  // -- portals -----------------------------------------------------------

  addPortal(p: Portal): void {
    this.portals.push(p)
  }

  /** Both directions in one call — the normal case for a door. */
  addPortalPair(
    a: Omit<Portal, 'id'> & { id: string },
    backRot?: Rot,
  ): void {
    this.addPortal(a)
    this.addPortal({
      id: `${a.id}:back`,
      sceneId: a.toSceneId,
      cell: a.toCell,
      toSceneId: a.sceneId,
      toCell: a.cell,
      ...(backRot === undefined ? {} : { toRot: backRot }),
    })
  }

  portalsIn(sceneId: SceneId): Portal[] {
    return this.portals.filter((p) => p.sceneId === sceneId)
  }

  portalAt(sceneId: SceneId, cell: Cell): Portal | null {
    return (
      this.portals.find(
        (p) =>
          p.sceneId === sceneId &&
          p.cell.gx === cell.gx &&
          p.cell.gz === cell.gz &&
          p.cell.gy === cell.gy,
      ) ?? null
    )
  }

  removePortalsFor(sceneId: SceneId): void {
    for (let i = this.portals.length - 1; i >= 0; i--) {
      const p = this.portals[i]
      if (p !== undefined && (p.sceneId === sceneId || p.toSceneId === sceneId)) {
        this.portals.splice(i, 1)
      }
    }
  }

  // -- activation --------------------------------------------------------

  /** Boot-time only: make a scene active with no fade and no events. */
  setActiveImmediate(id: SceneId): SceneNode {
    const node = this.require(id)
    for (const n of this.scenes.values()) {
      if (n === node) continue
      if (n.root.parent !== null) this.root.remove(n.root)
      n.root.visible = false
      n.awake = false
    }
    if (node.root.parent === null) this.root.add(node.root)
    node.root.visible = true
    node.awake = true
    this.activeId = id
    return node
  }

  /**
   * THE door transition. Awaits the fade, swaps the graph, awaits the fade in.
   * Save writes are blocked for the whole duration.
   */
  async transitionTo(id: SceneId, opts: TransitionOptions = {}): Promise<SceneNode> {
    const target = this.require(id)
    if (this.busy) return target
    if (this.activeId === id) return target

    this.busy = true
    const from = this.activeId
    const fadeMs = opts.instant === true ? 0 : (opts.fadeMs ?? 260)
    bus.emit('scene:transitionStart', { from, to: id })

    try {
      if (fadeMs > 0 && this.fader !== null) await this.fader('out', fadeMs)

      if (from !== null) {
        const prev = this.require(from)
        prev.root.visible = false
        prev.awake = false
        if (prev.root.parent !== null) this.root.remove(prev.root)
        bus.emit('scene:left', { scene: from })
      }

      if (target.root.parent === null) this.root.add(target.root)
      target.root.visible = true
      target.awake = true
      this.activeId = id
      bus.emit('scene:entered', { scene: id })
      // Tell whoever owns the player WHERE to stand. `spawnCell` has been part
      // of this signature from the start and was silently dropped on the floor;
      // this is the one line that makes a door actually put you in the room.
      bus.emit('scene:spawnAt', { scene: id, spawn: opts.spawnCell ?? null })

      if (fadeMs > 0 && this.fader !== null) await this.fader('in', fadeMs)
      return target
    } finally {
      this.busy = false
      bus.emit('scene:transitionEnd', { scene: id })
    }
  }

  /** Walk through the portal on `cell`, if there is one. Returns false if not. */
  async usePortalAt(cell: Cell, opts: TransitionOptions = {}): Promise<boolean> {
    if (this.activeId === null) return false
    const p = this.portalAt(this.activeId, cell)
    if (p === null) return false
    await this.transitionTo(p.toSceneId, { spawnCell: p.toCell, ...opts })
    return true
  }

  // -- teardown ----------------------------------------------------------

  /** Free a scene's GPU resources. Only for scenes a kid can never return to. */
  dispose(id: SceneId): void {
    const n = this.scenes.get(id)
    if (n === undefined) return
    n.root.traverse((o) => {
      const mesh = o as Partial<THREE.Mesh>
      mesh.geometry?.dispose?.()
      const m = mesh.material
      if (Array.isArray(m)) for (const mm of m) mm.dispose()
      else m?.dispose?.()
    })
    if (n.root.parent !== null) this.root.remove(n.root)
    this.scenes.delete(id)
    this.removePortalsFor(id)
    if (this.activeId === id) this.activeId = null
  }
}

export const world = new WorldGraph()
